import {
    IContextualMenuProps,
    ContextualMenu,
    PrimaryButton,
    Dialog,
    DialogType,
} from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import { appStyles } from '../../../helpers';
import { useAppDispatch, useAppSelector } from '../../../store';
import { selectTranslate } from '../../../store/selectors/ui';
import { CreateFolderForm } from '../../form/createFolderForm';
import {
    chunkedUploadAbortControllerRefBox,
    uploadFileThunk,
    UploadFileThunkError,
    uploadLargeFileThunk,
} from '../../../store/actions/app/thunks';
import {
    selectAppConfig,
    selectServerFiles,
} from '../../../store/selectors/app';
import { ProgressDialog } from '../../dialogs';
import { useFileProcessor } from '../../../helpers/hooks';
import { ErrOption } from '../../../helpers/model/errOption';
import { showNotification } from '../../../store/actions/app/actionCreators';
import {
    AppErrorCode,
    NotificationStatus,
} from '../../../store/actions/app/actionTypes';
import { ApiResponse } from 'nextcloud-api';
import { WebdavApiErrorSubCode } from 'nextcloud-api/dist/models/error-model';
import { appActions } from '../../../store/actions/app/actions';
import { MEGABYTE } from '../../../helpers/constants';

type Props = {
    pathForUpload: string;
};

export const UploadMenu = ({ pathForUpload }: Props): JSX.Element => {
    const [isFolderDialogHidden, setIsFolderDialogHidden] = useState(true);
    const [progressDialogHidden, setProgressDialogHidden] = useState(true);

    const onShowCreateFolderDialog = (): void => {
        setIsFolderDialogHidden(false);
    };

    const closeCreateFolderDialog = (): void => setIsFolderDialogHidden(true);

    const appConfig = useAppSelector(selectAppConfig);
    const translate = useAppSelector(selectTranslate);

    const {
        openFileSelector,
        plainFiles,
        clear: clearFiles,
    } = useFileProcessor();

    const dispatch = useAppDispatch();
    useEffect(() => {
        const file = plainFiles[0];
        if (!file) {
            return;
        }

        const uploadFile = async (): Promise<void> => {
            let res:
                | ApiResponse
                | ErrOption<UploadFileThunkError | WebdavApiErrorSubCode>;
            if (file.size < appConfig.minChunkedUploadFileSizeMb * MEGABYTE) {
                res = await dispatch(
                    uploadFileThunk({
                        folder: pathForUpload,
                        content: await file.arrayBuffer(),
                        fileBase: file.name,
                    }),
                );
            } else {
                setProgressDialogHidden(false);
                res = await dispatch(
                    uploadLargeFileThunk({
                        folder: pathForUpload,
                        file: file,
                        fileBase: file.name,
                    }),
                );
                setProgressDialogHidden(true);
            }

            if (res instanceof Error) {
                console.error(res);
                dispatch(appActions.clearFailedChunkedFileUploadData());

                switch (res.cause) {
                    case UploadFileThunkError.FileExists:
                        dispatch(
                            showNotification({
                                errorCode: AppErrorCode.CustomError,
                                message: translate('file_already_exists'),
                                status: NotificationStatus.Error,
                            }),
                        );
                        return;
                    case WebdavApiErrorSubCode.NetworkError:
                        dispatch(
                            showNotification({
                                errorCode: AppErrorCode.CustomError,
                                message: res.message,
                                status: NotificationStatus.Error,
                            }),
                        );
                        return;
                    default:
                        return clearFiles();
                }
            }

            clearFiles();
        };
        uploadFile();
    }, [plainFiles[0]]);

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'uploadFile',
                text: translate('secure_upload_files'),
                onClick: openFileSelector,
            },
            {
                key: 'createFolder',
                text: translate('button_newfolder'),
                onClick: onShowCreateFolderDialog,
            },
        ],
        isBeakVisible: false,
        useTargetWidth: true,
    };

    const getMenu = (props: IContextualMenuProps): JSX.Element => {
        return <ContextualMenu {...props} />;
    };

    const cancelUploadAndHideDialog = (isHidden) => {
        const controller = chunkedUploadAbortControllerRefBox.getValue();
        controller.abort('User cancelled upload');
        setProgressDialogHidden(isHidden);
        dispatch(appActions.clearFailedChunkedFileUploadData());
    };

    return (
        <>
            <PrimaryButton
                styles={appStyles.primaryButton}
                menuProps={menuProps}
                menuAs={getMenu}
                text={translate('add_new')}
            />
            <Dialog
                styles={appStyles.DialogStyle}
                hidden={isFolderDialogHidden}
                onDismiss={closeCreateFolderDialog}
                dialogContentProps={{ type: DialogType.close }}
            >
                <CreateFolderForm
                    pathForCreation={pathForUpload}
                    onCancel={closeCreateFolderDialog}
                />
            </Dialog>
            <ProgressDialog
                hidden={progressDialogHidden}
                onDismiss={cancelUploadAndHideDialog}
                title={translate('progress_header')}
                subText={translate('progress_count')}
            />
        </>
    );
};

export const CurrentFolderUploadMenu = (): JSX.Element => {
    const serverFiles = useAppSelector(selectServerFiles) ?? [];
    const currentFolderFile = serverFiles[0];
    const uploadPath = currentFolderFile?.parentPath ?? '';

    return <UploadMenu pathForUpload={uploadPath} />;
};
